import {
  ConfirmationDialog,
  FormFirstStep,
  FormFirstStepAreaController,
  FormFirstStepMaintenanceAdmin,
  FormFirstStepMaintenanceEngineer,
  FormSecondStep,
  FormSecondStepAreaController,
  FormSecondStepMaintenanceAdmin,
  FormSecondStepMaintenanceEngineer,
} from '@/components/index'
import { useFormStepsContext } from '@/context/FormStepsContext'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  ButtonProps,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from '@mui/material'
import { FC, useState } from 'react'

import SubmitCheck from '@/assets/icons/submit-check.svg'
import { useForm, useTicketState } from '@/utilities'
import { StepperType } from '@/utilities/types/ticket'
interface ActionButtonProps extends ButtonProps {
  bgcolor: string
}

const ActionButton = styled(Button)<ActionButtonProps>(({ bgcolor }) => ({
  paddingTop: 1.5,
  paddingBottom: 1.5,
  padding: '1.5 4 1.5 4',
  backgroundColor: bgcolor,
}))

const steps = ['Capture', 'Review', 'Confirmation']

type AlertSeverity = 'success' | 'error' | 'warning' | 'info'


interface FormStepperProps {
  data?: any
  show: boolean
  handleClose: () => void
  type: StepperType
  handleUpdateByBm: any
  dispatch: any
  mutateBmProcessed: () => Promise<any>
  handleUpdateByAc: any
  mutateForProcessing: () => Promise<any>
  handleCreateTicket: any
  bmState: ReturnType<typeof useTicketState>
  handleUpdateByMe: any
  handleUpdateByMa: any
  handleAlertMessage: (message: string, severity: AlertSeverity) => void
}

export const Success: FC<{ type: StepperType }> = ({ type }) => {
  return (
    <Box textAlign="center" mt={4}>
      <img src={SubmitCheck} alt="Edit" width={50} height={50} />
      <Typography fontSize={'30px'} fontWeight={400} color="#2B2A2A">
        {type === 'Create'
          ? 'You have submitted a ticket!'
          : 'You have updated a ticket!'}
      </Typography>
    </Box>
  )
}

export const FormStepper: FC<FormStepperProps> = ({
  show,
  handleClose,
  data,
  type,
  handleUpdateByBm,
  dispatch,
  mutateBmProcessed,
  handleUpdateByAc,
  mutateForProcessing,
  handleCreateTicket,
  bmState,
  handleUpdateByMe,
  handleUpdateByMa,
  handleAlertMessage,
}) => {
  const [activeStep, setActiveStep] = useState(0)
  const [skipped, setSkipped] = useState(new Set<number>())
  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')


  const [loading, setLoading] = useState(false)

  const { formData, setFormData, role } = useFormStepsContext()

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [completedSteps, setCompletedSteps] = useState<Record<number, boolean>>(
    {},
  )

  const [initialFormData, setInitialFormData] = useState(null)

  const isFormChanged =
    JSON.stringify(initialFormData) !== JSON.stringify(formData)

  const validateFields = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.typeOfConcern) {
      newErrors.typeOfConcern = 'Type of Concern is required'
    }

    if (formData.typeOfConcern && !formData.typeOfConcernValue) {
      newErrors.typeOfConcernValue = `Type of ${formData.typeOfConcern} is required`
    }

    if (
      formData.typeOfConcern &&
      formData.typeOfConcernValue === 'Others' &&
      !formData.typeOfConcernOthers
    ) {
      newErrors.typeOfConcernOthers = 'Others is required'
    }

    if (formData.purpose) {
      if (!formData.selectedRepairMethod) {
        newErrors.selectedRepairMethod = 'Method of Repair is required'
      }

      if (!formData.costOfSharing) {
        newErrors.costOfSharing = 'Cost Sharing is required'
      }
    }

    if (!formData.costAmount || parseFloat(formData.costAmount) <= 0) {
      // newErrors.costAmount = 'Valid Cost Amount is required'
    }

    if (!formData.description) {
      newErrors.description = 'Description is required'
    }

    if (
      (!formData.concernAttachments ||
        formData.concernAttachments.length === 0) &&
      type === 'Create'
    ) {
      newErrors.concernAttachments = 'At least one attachment is required.'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateFieldsForAc = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.typeOfConcern) {
      newErrors.typeOfConcern = 'Type of Concern is required'
    }

    if (formData.typeOfConcern && !formData.typeOfConcernValue) {
      newErrors.typeOfConcernValue = `Type of ${formData.typeOfConcern} is required`
    }

    if (
      formData.typeOfConcern &&
      formData.typeOfConcernValue === 'Others' &&
      !formData.typeOfConcernOthers
    ) {
      newErrors.typeOfConcernOthers = 'Others is required'
    }

    if (!formData.selectedDealer) {
      newErrors.selectedDealer = 'Dealer is required'
    }

    if (!formData.selectedBranchDealer) {
      newErrors.selectedBranchDealer = 'Branch is required'
    }

    if (!formData.purpose) {
      newErrors.purpose = 'Purpose is required'
    }

    if (formData.purpose) {
      if (!formData.selectedRepairMethod) {
        newErrors.selectedRepairMethod = 'Method of Repair is required'
      }

      if (!formData.costOfSharing) {
        newErrors.costOfSharing = 'Cost Sharing is required'
      }
    }

    if (!formData.costAmount || parseFloat(formData.costAmount) <= 0) {
      // newErrors.costAmount = 'Valid Cost Amount is required'
    }

    if (!formData.description) {
      newErrors.description = 'Description is required'
    }

    if (
      (!formData.concernAttachments ||
        formData.concernAttachments.length === 0) &&
      type === 'Create'
    ) {
      newErrors.concernAttachments = 'At least one attachment is required.'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateFieldsForMe = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.purpose) {
      newErrors.purpose = 'Purpose is required'
    }

    if (formData.purpose) {
      if (!formData.selectedRepairMethod) {
        newErrors.selectedRepairMethod = 'Method of Repair is required'
      }

      if (!formData.costOfSharing) {
        newErrors.costOfSharing = 'Cost Sharing is required'
      }
    }

    if (!formData.costAmount || parseFloat(formData.costAmount) <= 0) {
      // newErrors.costAmount = 'Valid Cost Amount is required'
    }

    if (!formData.typeOfConcern) {
      newErrors.typeOfConcern = 'Type of Concern is required'
    }

    if (formData.typeOfConcern && !formData.typeOfConcernValue) {
      newErrors.typeOfConcernValue = `Type of ${formData.typeOfConcern} is required`
    }

    if (
      formData.typeOfConcern &&
      formData.typeOfConcernValue === 'Others' &&
      !formData.typeOfConcernOthers
    ) {
      newErrors.typeOfConcernOthers = 'Others is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    // if (!isFormChanged && type === 'Create') {
    //   handleAlertMessage("No changes detected!", "error");
    //   return;
    // }

    if (role === 'Branch Manager' || role === 'OIC') {
      if (activeStep === 0 && !validateFields()) {
        return
      }
    }

    if (role === 'Area Controller') {
      if (activeStep === 0 && !validateFieldsForAc()) {
        return
      }
    }

    if (role === 'Maintenance Engineer') {
      if (activeStep === 0 && !validateFieldsForMe()) {
        return
      }
    }

    setCompletedSteps((prev) => ({ ...prev, [activeStep]: true }))
    setActiveStep((prev) => prev + 1)
  }

  const handleBack = () => setActiveStep((prev) => prev - 1)

  const prepareDataPayload = () => {
    const baseData: Record<string, any> = {
      // typeOfConcern: formData?.selectedConcern,
      // typeOfAppliance: formData?.selectedAppliance ?? '',
      // typeOfCollateral: formData?.selectedCollateral ?? '',
      typeOfConcern: formData.typeOfConcern,
      typeOfConcernValue: formData.typeOfConcernValue,
      typeOfConcernOthers: formData.typeOfConcernOthers,
      amount: parseFloat((formData?.costAmount || '0').replace(/,/g, '')),
      description: formData.description,
    }

    if (type === 'Create') {
      baseData.branchId =
        role === 'Branch Manager' || role === 'OIC'
          ? formData?.branchId
          : formData?.selectedBranchDealer?.branchId
    }

    if (
      role === 'Area Controller' ||
      role === 'Branch Manager' ||
      role === 'OIC'
    ) {
      return {
        ...baseData,
        costSharing: formData?.costOfSharing,
        methodOfRepair: formData?.selectedRepairMethod,
        purpose: formData?.purpose,
      }
    }

    if (role === 'Maintenance Engineer') {
      return {
        ...baseData,
        costSharing: formData?.costOfSharing,
        methodOfRepair: formData?.selectedRepairMethod,
        purpose: formData?.purpose,
        // description: formData.description,
        // amount: parseFloat((formData?.costAmount || '0').replace(/,/g, '')),
        isApprove: false,
      }
    }
    return baseData
  }
  const handleApiError = (error: any, attachmentType?: string) => {
    console.log('🚨 handleApiError called')
    console.log('Full error object:', error)
    console.log('error.response:', error?.response)
    console.log('error.response.status:', error?.response?.status)
    console.log('error.response.data:', error?.response?.data)
    
    const status = error?.response?.status || error?.status
    const apiMessage = error?.response?.data?.message || error?.message
    
    console.log('Extracted status:', status)
    console.log('Extracted message:', apiMessage)
  
    if (status === 422) {
      console.log(' Setting error modal for 422')
      const attachmentTypeText = attachmentType ? ` ${attachmentType}` : ' attach files'
      setErrorMessage(
        `Something went wrong while processing the${attachmentTypeText}.`
      )
      setErrorModalOpen(true)
      console.log('Error modal state set to true')
      return
    }
  
    console.log(' Setting error modal for other error')
    setErrorMessage('Something went wrong. Please try again later.')
    setErrorModalOpen(true)
    console.log('Error modal state set to true')
  }
  
  const handleFileTicket = async () => {
    try {
      if (activeStep === 1 && type === 'Create') {
        const formDataPayload = new FormData()
        formDataPayload.append(
          'jsonData',
          JSON.stringify(prepareDataPayload()),
        )
  
        formData.concernAttachments?.forEach((file) =>
          formDataPayload.append('concernsFiles', file),
        )
        formData.quotationAttachments?.forEach((file) =>
          formDataPayload.append('quotationFiles', file),
        )
  
        await handleCreateTicket(
          formDataPayload,
          role === 'Branch Manager' || role === 'OIC' ? 'bm' : 'ac',
          setLoading,
          setActiveStep,
        )(dispatch)
  
        role === 'Branch Manager' || role === 'OIC'
          ? mutateBmProcessed()
          : mutateForProcessing()
  
        setTimeout(handleClose, 3000)
      } else if (
        activeStep === 1 &&
        type === 'Update' &&
        (role === 'Branch Manager' || role === 'OIC')
      ) {
        const formDataPayload = new FormData()
        const dtoParam = encodeURIComponent(
          JSON.stringify(prepareDataPayload()),
        )
  
        formData.concernAttachments?.forEach((file) =>
          formDataPayload.append('concernsFiles', file),
        )
        formData.quotationAttachments?.forEach((file) =>
          formDataPayload.append('quotationFiles', file),
        )
  
        await handleUpdateByBm(
          formDataPayload,
          data.id,
          dtoParam,
          setLoading,
          setActiveStep,
        )(dispatch)
  
        mutateBmProcessed()
        setTimeout(handleClose, 3000)
      } else if (
        activeStep === 1 &&
        type === 'Update' &&
        role === 'Area Controller'
      ) {
        const formDataPayload = new FormData()
        const dtoParam = encodeURIComponent(
          JSON.stringify(prepareDataPayload()),
        )
  
        formData.concernAttachments?.forEach((file) =>
          formDataPayload.append('concernsFiles', file),
        )
        formData.quotationAttachments?.forEach((file) =>
          formDataPayload.append('quotationFiles', file),
        )
  
        await handleUpdateByAc(
          formDataPayload,
          data.id,
          dtoParam,
          setLoading,
          setActiveStep,
        )(dispatch)
  
        mutateForProcessing()
        setTimeout(handleClose, 3000)
      } else if (
        activeStep === 1 &&
        type === 'Update' &&
        role === 'Maintenance Engineer'
      ) {
        const dtoParam = encodeURIComponent(
          JSON.stringify(prepareDataPayload()),
        )
  
        await handleUpdateByMe(
          prepareDataPayload(),
          data.id,
          dtoParam,
          setLoading,
          setActiveStep,
        )(dispatch)
  
        mutateForProcessing()
        setTimeout(handleClose, 3000)
      } else if (
        activeStep === 1 &&
        type === 'Update' &&
        role === 'Maintenance Admin'
      ) {
        await handleUpdateByMa(
          data.id,
          parseFloat((formData?.costAmount || '0').replace(/,/g, '')),
          setLoading,
          setActiveStep,
        )(dispatch)
  
        mutateForProcessing()
        setTimeout(handleClose, 3000)
      } else {
        handleNext()
      }
    } catch (error) {
      setLoading(false)
      
      // Determine attachment type based on what attachments are present
      let attachmentContext = ''
      if (formData.concernAttachments?.length > 0 && formData.quotationAttachments?.length > 0) {
        attachmentContext = 'Concern and Quotation/Certificate/Receipt attachments'
      } else if (formData.concernAttachments?.length > 0) {
        attachmentContext = 'Concern attachments'
      } else if (formData.quotationAttachments?.length > 0) {
        attachmentContext = 'Quotation/Certificate of Completion/Official Receipt attachments'
      } else {
        attachmentContext = 'attachments'
      }
      
      handleApiError(error, attachmentContext)
    }
  }
  

  type Role =
    | 'Super Admin'
    | 'Branch Manager'
    | 'OIC'
    | 'Area Controller'
    | 'Maintenance Engineer'
    | 'Maintenance Admin'

  const stepComponents: Record<Role, FC<any>[]> = {
    'Super Admin': [FormFirstStep, FormSecondStep, Success],
    'Branch Manager': [FormFirstStep, FormSecondStep, Success],
    OIC: [FormFirstStep, FormSecondStep, Success],
    'Area Controller': [
      FormFirstStepAreaController,
      FormSecondStepAreaController,
      Success,
    ],
    'Maintenance Engineer': [
      FormFirstStepMaintenanceEngineer,
      FormSecondStepMaintenanceEngineer,
      Success,
    ],
    'Maintenance Admin': [
      FormFirstStepMaintenanceAdmin,
      FormSecondStepMaintenanceAdmin,
      Success,
    ],
  }

  const renderStepContent = () => {
    if (!role || !(role in stepComponents)) return null

    const StepComponent = stepComponents[role as Role]?.[activeStep]
    return activeStep === 2 ? (
      <StepComponent type={type} />
    ) : (
      <StepComponent
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        type={type}
        data={data}
        show={show}
        role={role}
        initialFormData={initialFormData}
        setInitialFormData={setInitialFormData}
        handleAlertMessage={handleAlertMessage}
      />
    )
  }

  const {
    showModal: showConfirmationDialog,
    handleAddData: handleOpenConfirmation,
    handleCloseModal: handleCloseConfirmation,
  } = useForm()

  return (
    <Dialog
      open={show || errorModalOpen}
      scroll="paper"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          maxWidth: errorModalOpen ? '400px' : (activeStep === 2 ? '500px' : '981px'),
          height: errorModalOpen ? '300px' : (activeStep === 2 ? '400px' : '900px'),
          padding: '1%',
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {errorModalOpen ? (
          // Error content
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mx: 4, my: 2 }}
            >
              <Typography
                sx={{
                  fontFamily: 'Gabarito',
                  fontWeight: 600,
                  fontSize: '22px',
                  color: '#d32f2f',
                }}
              >
                Upload Failed
              </Typography>
              <IconButton
                onClick={() => setErrorModalOpen(false)}
                sx={{ color: '#505470', width: 20, height: 20 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2, mx: 4, borderWidth: 2, color: '#B1B8BF' }} />

            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  textAlign: 'center',
                  px: 3,
                  color: '#2B2A2A',
                  fontSize: '16px',
                  lineHeight: 1.5
                }}
              >
                {errorMessage}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="flex-end" sx={{ px: '5%', pb: 2 }}>
              <Button
                variant="contained"
                onClick={() => setErrorModalOpen(false)}
                sx={{
                  backgroundColor: '#495AFF',
                  '&:hover': {
                    backgroundColor: '#3d4ed8',
                  },
                }}
              >
                OK
              </Button>
            </Box>
          </>
        ) : (
          // Normal stepper content
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mx: 4, my: 2 }}
            >
              <Typography
                sx={{
                  fontFamily: 'Gabarito',
                  fontWeight: 500,
                  fontSize: '22px',
                  color: '#505470',
                }}
              >
                {type === 'Create' ? 'File Ticket' : 'Edit Ticket'}
              </Typography>
              <IconButton
                onClick={activeStep === 2 ? handleClose : handleOpenConfirmation}
                sx={{
                  color: '#505470',
                  width: 20,
                  height: 20,
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2, mx: 4, borderWidth: 2, color: '#B1B8BF' }} />

            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                maxWidth: '650px',
                margin: '0 auto',
                width: '100%',
                px: { xs: 1, sm: 2, md: 4 },
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: { xs: 2, sm: 3 },
              }}
            >
              {steps.map((label, index) => (
                <Step key={label} completed={index < activeStep}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontFamily: 'Gabarito',
                        fontWeight: 600,
                        fontSize: { xs: 12, sm: 13, md: 14 },
                        color: '#495AFF',
                        transform: 'translateY(-10px)',
                        textAlign: 'center',
                      },
                      '& .MuiSvgIcon-root': {
                        width: { xs: 16, sm: 18, md: 20 },
                        height: { xs: 16, sm: 18, md: 20 },
                        mt: 0.5,
                        color: '#495AFF',
                      },
                      '& .MuiStepIcon-text': {
                        fontSize: { xs: 11, sm: 13 },
                        fontWeight: 500,
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
              {renderStepContent()}
            </Box>
            {activeStep !== 2 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  px: '5%',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Button
                  color="inherit"
                  disabled={activeStep === 0 || loading}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                <Button onClick={handleFileTicket} disabled={loading}>
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : activeStep === 1 ? (
                    type === 'Update' ? (
                      'Update'
                    ) : (
                      'Submit'
                    )
                  ) : (
                    'Next'
                  )}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
      <ConfirmationDialog
        open={showConfirmationDialog}
        type={type}
        onCancel={handleCloseConfirmation}
        onConfirm={handleClose}
      />
    </Dialog>
  )
}
